import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { toast } from 'react-toastify';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import {
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
} from '@material-ui/icons';
import { isArray, pickBy } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { TextField as Input } from '~/components/Form/v2';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from '../../styles';

export default function Card({ params }) {
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [timeOut, setTimeOut] = useState(0);
  const [search, setSearch] = useState({});

  const formRef = useRef(null);

  const currentPage = useMemo(
    () => (Number(page - 1) === 0 ? pageSize : Number(page - 1)),
    [page, pageSize]
  );

  const handleGetResponseAPI = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get('/alarmemonitoramento', {
        params: { ...params, ...search, page, pagesize: perPage },
        paramsSerializer: (param) => qs.stringify(param),
      });

      const numPage = response.data.page;
      const totalPage = response.data.totalPages;

      setPage(numPage < totalPage ? Number(numPage + 1) : 1);
      setPageSize(totalPage);
      setData(response.data.items);
    } catch (error) {
      if (isArray(error)) error.forEach((err) => toast.error(t(err)));
      else toast.error(t(error));
    } finally {
      setLoading(false);
    }
  }, [params, page, perPage, search]);

  const handleChangeInputPerPage = useCallback(({ target }) => {
    setPerPage(Number(target.value));
    setTimeOut(0);
    setPage(1);
  }, []);

  const handleSubmit = useCallback(async (formData) => {
    try {
      const wherefield = [];
      const valuewherefield = [];
      const typewherefield = [];

      Object.keys(pickBy(formData)).forEach((pick) => {
        wherefield.push(pick);
        valuewherefield.push(formData[pick]);
        typewherefield.push('LIKE');
      });

      setSearch({ wherefield, valuewherefield, typewherefield });
      setTimeOut(0);
      setPage(1);
    } catch (error) {
      if (isArray(error)) error.forEach((err) => toast.error(t(err)));
      else toast.error(t(error));
    }
  }, []);

  const handleKeyPressInput = useCallback(({ key }) => {
    if (key === 'Enter') formRef.current.submitForm();
  }, []);

  const handleNextOrPreviewPage = useCallback((number) => {
    setPageSize(0);
    setTimeOut(0);
    setPage(number);
  }, []);

  useEffect(() => {
    setPageSize(0);
    setTimeOut(0);
    setPage(1);
  }, [params]);

  useEffect(() => {
    const isTime = setInterval(() => {
      handleGetResponseAPI();
      setTimeOut(30000);
    }, timeOut);

    return () => clearInterval(isTime);
  }, [handleGetResponseAPI, timeOut]);

  return (
    <S.Container ref={formRef} onSubmit={handleSubmit}>
      {loading && <LinearProgress />}
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                Município
                <Input
                  name="muni_nome"
                  variant="filled"
                  size="small"
                  onKeyPress={handleKeyPressInput}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell>
                Origem
                <Input
                  name="origem"
                  variant="filled"
                  size="small"
                  onKeyPress={handleKeyPressInput}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell>
                Alarme
                <Input
                  name="aler_nota_mensagem_exibir"
                  variant="filled"
                  size="small"
                  onKeyPress={handleKeyPressInput}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell align="center">Tempo Ativo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={Math.random()}>
                <TableCell component="th" scope="row">
                  {item.muni_nome}
                </TableCell>
                <TableCell>{item.origem}</TableCell>
                <TableCell
                  style={{ color: '#FFFFFF', backgroundColor: item.tial_cor }}
                >
                  {item.aler_nota_mensagem_exibir}
                </TableCell>
                <TableCell align="center">
                  {item.aler_datahora_evento_ativo_fmt}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <S.Pagination>
        <Typography variant="body2">Registros por página:</Typography>

        <TextField
          variant="outlined"
          size="small"
          type="number"
          className="inputPerPage"
          defaultValue={perPage}
          disabled={loading}
          onMouseDown={handleChangeInputPerPage}
          max={99}
          min={1}
        />

        <Typography variant="body2">
          {currentPage} de {pageSize}
        </Typography>

        <S.ButtonGroup>
          <IconButton
            size="small"
            color="default"
            disabled={currentPage === 1 || loading}
            onClick={() => handleNextOrPreviewPage(Number(currentPage - 1))}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            size="small"
            color="default"
            disabled={currentPage === pageSize || loading}
            onClick={() => handleNextOrPreviewPage(page)}
          >
            <ChevronRight />
          </IconButton>
        </S.ButtonGroup>
      </S.Pagination>
    </S.Container>
  );
}

Card.propTypes = {
  params: PropTypes.objectOf(PropTypes.any),
};

Card.defaultProps = {
  params: {},
};
